import * as React from 'react';
import { useCallback, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useTheme } from '../../../../../theme';

import {
	InfoMessageTypeEnum,
	CheckinOrderStatus,
	CheckinOrderFragment,
	useCheckinRulesQuery,
	useCheckinCreateOrderMutation,
	useResetOrderPaymentBlockMutation,
	useGetCheckinOrderQuery,
	CheckinOrderCreateParameters
} from '@websky/graphql';
import RetrieveBooking from '../../../../../RetrieveBooking';
import { fillCheckinOrder } from '../../../store/order/actions';
import { AcceptCheckinRules } from '@features/start-check-in';
import { captureErrorSentry } from '../../../../../utils';
import { Mode } from '../../../../../RetrieveBooking/components/RetrieveBooking';
import { useLocation } from 'react-router-dom';
import { useConfig } from '../../../../../context';
import { NULL_COMPONENT, useRenderers } from '../../../../../renderProps';
import { useToggleable } from '../../../../../hooks';
import Conditions from '../ConditionsModal/Conditions/Conditions';
import { useCheckinError } from '../../../hooks/useCheckinError';

interface RouteParams {
	PNR?: string;
	lastName?: string;
}

const Initial: React.FC<RouteComponentProps> = ({ match, history }) => {
	const { Inital: css } = useTheme('Checkin');
	const { t } = useTranslation('Checkin');

	const dispatch = useDispatch();

	const params: RouteParams = match.params;
	const location = useLocation();
	const isAutoSubmit = location.pathname.includes('submit');

	const { isOpen: rulesModalIsOpen, open: openModal, close: closeModal } = useToggleable(false);
	const [checkinRules, setCheckinRules] = useState<string | null>(null);

	const {
		CheckinNotification: CheckinNotificationOverride = NULL_COMPONENT,
		renderCheckinHint: RenderCheckinHintOverride = NULL_COMPONENT
	} = useRenderers();

	const { handleError: handleCheckinError } = useCheckinError();

	const [createCheckin] = useCheckinCreateOrderMutation();
	const [resetOrderPaymentBlock] = useResetOrderPaymentBlockMutation();

	const { refetch: getCheckinRules } = useCheckinRulesQuery({
		skip: true,
		fetchPolicy: 'no-cache'
	});

	const { refetch: getOrder } = useGetCheckinOrderQuery({ skip: true, fetchPolicy: 'no-cache' });

	const {
		global: {
			companyInfo: { iataCode }
		}
	} = useConfig();

	let order: CheckinOrderFragment;

	const openCheckinPage = useCallback(() => {
		if (order) {
			dispatch(fillCheckinOrder(order));
			history.push(`/${order.id}`);
		}
	}, [dispatch, history]);

	const onSumbit = useCallback(
		async (params: CheckinOrderCreateParameters): Promise<CheckinOrderFragment | boolean | undefined> => {
			try {
				const { data, errors } = await createCheckin({ variables: { params } });

				if (errors?.length && handleCheckinError(errors)) {
					/** чтобы в RetrieveBooking не отображалось ошибка "Order not found" под инпутом  */
					return true;
				}

				let result = !errors && data?.CheckinCreateOrder;

				if (!result) {
					return undefined;
				}

				if (result.isBlockedForPayment) {
					await resetOrderPaymentBlock({ variables: { id: result.id } });
					const { data: orderData, errors: orderErrors } = await getOrder({ id: result.id });

					if (orderErrors || !orderData?.CheckinOrder) {
						throw new Error('GetCheckinOrder failed');
					}

					result = orderData.CheckinOrder;
					order = result;

					return result;
				}

				order = result;

				if (result.status === CheckinOrderStatus.Confirmed || iataCode === 'N4') {
					openCheckinPage();
				} else {
					const { data: rulesData, errors: rulesErrors } = await getCheckinRules({ id: result.id });
					const rules = rulesData?.CheckinRules;

					if (rulesErrors || !rules) {
						captureErrorSentry(
							rulesErrors
								? `CheckinRules query failed, skipping rules modal. orderId=${result.id}`
								: `CheckinRules is empty, skipping rules modal. orderId=${result.id}`,
							'warning'
						);
						openCheckinPage();
					} else {
						setCheckinRules(rules);
						openModal();
					}
				}

				return result;
			} catch (e) {
				if (handleCheckinError(e)) {
					/** чтобы в RetrieveBooking не отображалось ошибка "Order not found" под инпутом  */
					return true;
				}

				throw e;
			}
		},
		[
			createCheckin,
			resetOrderPaymentBlock,
			getOrder,
			getCheckinRules,
			iataCode,
			openCheckinPage,
			openModal,
			handleCheckinError
		]
	);

	const acceptRules = useCallback(() => {
		closeModal();

		openCheckinPage();
	}, []);

	return (
		<div className={css.wrapper}>
			<div className={css.content}>
				<Conditions className={css.conditions} />

				<RetrieveBooking
					mode={Mode.Checkin}
					className={css.form}
					title={t('Online check-in')}
					initialId={params.PNR}
					initialSecret={params.lastName}
					submitWithInitialValues={isAutoSubmit}
					onSubmit={onSumbit}
					idTitle={t('Ticket number or booking reference')}
					secretTitle={t('Passenger last name')}
				/>
			</div>

			<CheckinNotificationOverride
				infoMessage={{
					code: null,
					type: InfoMessageTypeEnum.Promo,
					text: t('To check in for the Yamal Airlines flight, please visit their official website')
				}}
				type="global"
				status={InfoMessageTypeEnum.Promo}
				actionText={t('Go to the website')}
				onClickAction={() => window.open('https://yamal.aero/check-in/?wx#/')}
			/>

			<RenderCheckinHintOverride className={css.hint} />

			{checkinRules && (
				<AcceptCheckinRules
					isOpen={rulesModalIsOpen}
					onClose={closeModal}
					classes={{
						container: css.modal
					}}
					onSubmit={acceptRules}
					rules={checkinRules}
				/>
			)}
		</div>
	);
};

export default withRouter(Initial);
